import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, GearSix } from "@phosphor-icons/react";
import { BackupSection } from "@/components/settings/BackupSection";
import { SyncSection } from "@/components/settings/SyncSection";
import { UserManagementSection } from "@/components/settings/UserManagementSection";
import { useAuth } from "@/contexts/AuthContext";

export default function Settings() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Office users get scoped access - only the Sync tab is theirs. Backup and
  // User Management remain admin-only.
  const defaultTab = isAdmin ? "backup" : "sync";
  const requestedTab = searchParams.get("tab");
  const tab = !isAdmin && requestedTab !== "sync" ? "sync" : requestedTab || defaultTab;

  return (
    <div className="flex h-screen w-screen flex-col overflow-x-hidden bg-white" data-testid="settings-page">
      <header className="flex items-center gap-3 border-b border-zinc-300 px-4 py-3 lg:px-6">
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm border-zinc-300"
          onClick={() => navigate("/")}
          data-testid="back-to-dashboard-button"
        >
          <ArrowLeft size={14} className="mr-1.5" /> Dashboard
        </Button>
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-sm border border-zinc-300 bg-zinc-50">
            <GearSix size={16} weight="bold" className="text-blue-600" />
          </div>
          <h1 className="font-heading text-lg font-bold tracking-tight text-zinc-950">Settings</h1>
        </div>
      </header>

      <div className="min-w-0 flex-1 overflow-auto p-4 lg:p-6">
        <Tabs value={tab} onValueChange={(v) => setSearchParams({ tab: v })}>
          <TabsList className="rounded-sm">
            {isAdmin && (
              <TabsTrigger value="backup" className="rounded-sm" data-testid="settings-tab-backup">
                Backup
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="users" className="rounded-sm" data-testid="settings-tab-users">
                Users
              </TabsTrigger>
            )}
            <TabsTrigger value="sync" className="rounded-sm" data-testid="settings-tab-sync">
              Sync
            </TabsTrigger>
          </TabsList>
          {isAdmin && (
            <TabsContent value="backup" className="mt-6">
              <BackupSection />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="users" className="mt-6">
              <UserManagementSection />
            </TabsContent>
          )}
          <TabsContent value="sync" className="mt-6">
            <SyncSection />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
